import { useEffect, useState } from 'react';
import { fetchImageBlob } from '../api/endpoints';

/**
 * Renders a bearer-protected document image.
 *
 * The Razor portal could point <img src="/images/12"> straight at the server because the
 * browser attached the auth cookie automatically. With token auth it cannot, so the bytes are
 * fetched through the authenticated axios client and handed to the <img> as an object URL.
 * The URL is revoked on unmount to avoid leaking blobs.
 */
interface AuthImageProps {
  /** DocumentImageItem.externalId — an opaque GUID, not the internal row id. */
  imageId: string;
  alt: string;
  className?: string;
}

export function AuthImage({ imageId, alt, className }: AuthImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    fetchImageBlob(imageId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageId]);

  if (failed) {
    return (
      <div
        className={`d-flex align-items-center justify-content-center border rounded text-body-secondary ${className ?? ''}`}
        style={{ minHeight: '120px', minWidth: '160px' }}
      >
        <i className="bi bi-image me-1" /> Unavailable
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className={`d-flex align-items-center justify-content-center border rounded ${className ?? ''}`}
        style={{ minHeight: '120px', minWidth: '160px' }}
      >
        <div className="spinner-border spinner-border-sm text-secondary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>
      </div>
    );
  }

  return <img src={url} alt={alt} className={className} />;
}
